// 타임라인 시간축 순수함수 — 정수 UTC day-index 단일 출처 (Design §5.2 결정 1~6).
// 불변: 시간 = 정수 UTC day-index. epoch-ms 부동소수 금지. 로컬자정 파싱(new Date(iso+"T00:00:00")) 금지 — DST 지역에서 2일이 1.958일이 된다 (결정1).
// 불변: todayKst() 외 모든 함수는 시계를 읽지 않는다 — today 는 인자로 받는다. TZ 안전 + 테스트 결정성 (결정2).
// 불변: 바 = [start, end+1) 반열린 → 하루짜리 바도 폭 > 0. 오프바이원의 유일 발생점 (결정4). 마일스톤 = 셀 중앙 (결정5).
// 불변: Scale 에 픽셀 없음 — 렌더는 %, 드래그만 px. 반응형 재측정 불필요 (결정6).
// 테스트 잠금(scale.test.ts): KST 경계 14:59:59Z→당일 / 15:00:00Z→익일 · 2028-02-29 윤일 · 2026-07-16~2027-02-07 = spanDays 207 / 월 눈금 8개("26.07"~"27.02") · 하루 바 width > 0.

/** 시간축 — 픽셀 없음. originDay = 좌단 day-index, spanDays = 표시 일수(양끝 포함, 최소 1). */
export interface Scale {
  originDay: number;
  spanDays: number;
}

/** 월 눈금 — dayIndex 는 달 1일(스케일 좌단보다 앞서면 좌단으로 클램프). */
export interface MonthTick {
  dayIndex: number;
  label: string;
  isFuture: boolean;
}

const DAY_MS = 86_400_000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * UTC epoch ms → KST(+9) 기준 날짜 "YYYY-MM-DD".
 * todayKst() 의 순수 코어 — 시계를 인자로 받으므로 테스트가 실행 머신 TZ에 의존하지 않는다 (Design §5.2 결정2).
 */
export function kstDateFromUtcMs(ms: number): string {
  return new Date(ms + KST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * KST 기준 오늘 "YYYY-MM-DD".
 * 이 모듈에서 시계를 읽는 유일한 함수. `new Date().toISOString().slice(0,10)` 은 UTC라서
 * KST 오전 9시 이전엔 **어제**를 준다 (FlowPage.tsx:143 실버그 — Design §5.3).
 */
export function todayKst(): string {
  return kstDateFromUtcMs(Date.now());
}

/**
 * "YYYY-MM-DD" → UTC 자정 기준 정수 day-index.
 * 롤오버("2026-02-31" → 3/3) 는 조용히 통과시키지 않고 throw — 무성한 NaN 기하보다 시끄러운 실패가 낫다.
 */
export function toDayIndex(iso: string): number {
  const m = ISO_RE.exec(iso);
  if (!m) throw new Error(`[scale] invalid ISO date: ${iso}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const n = Date.UTC(y, mo - 1, d) / DAY_MS;
  if (!Number.isFinite(n)) throw new Error(`[scale] invalid ISO date: ${iso}`);
  const idx = Math.round(n);
  // 왕복 검증 — 존재하지 않는 날짜(2026-02-31 / 2026-13-01)의 조용한 롤오버 차단
  if (fromDayIndex(idx) !== iso.slice(0, 10)) throw new Error(`[scale] invalid ISO date: ${iso}`);
  return idx;
}

/** 정수 day-index → "YYYY-MM-DD". toDayIndex 의 역함수. */
export function fromDayIndex(n: number): string {
  return new Date(Math.round(n) * DAY_MS).toISOString().slice(0, 10);
}

/** iso 에서 n일 이동한 날짜. 월말·윤일을 Date.UTC 가 처리한다 (2028-02-28 +1 = 2028-02-29). */
export function addDays(iso: string, n: number): string {
  return fromDayIndex(toDayIndex(iso) + Math.round(n));
}

/** b − a 의 일수 차. 같은 날 = 0. */
export function diffDays(a: string, b: string): number {
  return toDayIndex(b) - toDayIndex(a);
}

/**
 * 스케일 생성. spanDays 는 양끝 포함(= diffDays + 1) — 마지막 날도 온전한 한 칸을 갖는다.
 * end < start 여도 throw 하지 않고 spanDays 를 최소 1로 클램프한다.
 */
export function makeScale(startIso: string, endIso: string): Scale {
  const originDay = toDayIndex(startIso);
  const endDay = toDayIndex(endIso);
  return { originDay, spanDays: Math.max(endDay - originDay + 1, 1) };
}

/** day-index → 좌단 기준 0~100 백분율. 정수가 아닌 값(셀 중앙 등)도 허용. */
export function dayToPct(dayIndex: number, s: Scale): number {
  return ((dayIndex - s.originDay) / s.spanDays) * 100;
}

/** 날짜 → 그 날 칸의 **좌단** 백분율. */
export function dateToPct(iso: string, s: Scale): number {
  return dayToPct(toDayIndex(iso), s);
}

/**
 * 바의 좌단·폭 백분율. 반열린 [start, end+1) 이므로 start === end 인 하루짜리 바도 폭 > 0 (Design §5.2 결정4).
 * end < start 는 뒤집지 않고 start 로 클램프한다 (결정9 — DB CHECK 와 동일 문장).
 */
export function barPct(startIso: string, endIso: string, s: Scale): { left: number; width: number } {
  const startDay = toDayIndex(startIso);
  const endDay = Math.max(toDayIndex(endIso), startDay);
  const left = dayToPct(startDay, s);
  return { left, width: dayToPct(endDay + 1, s) - left };
}

/** 마일스톤의 백분율 = 그 날 칸의 **중앙** (Design §5.2 결정5 — 좌단이면 오늘선/전날 바와 겹쳐 판독 불가). */
export function milestonePct(iso: string, s: Scale): number {
  return dayToPct(toDayIndex(iso) + 0.5, s);
}

/**
 * 픽셀 이동량 → 반올림된 정수 일수. 스냅 = 1일 고정 (Design §5.2 결정3).
 * 좌우 대칭 반올림(half-away-from-zero) — Math.round 는 -0.5 를 -0 으로 만들어 왼쪽 드래그만 둔해진다.
 * widthPx 가 0/음수/비유한이면 0 (드래그 중 예외 금지 — 결정11).
 */
export function pxToDays(dx: number, widthPx: number, s: Scale): number {
  if (!Number.isFinite(dx) || !Number.isFinite(widthPx) || widthPx <= 0) return 0;
  const raw = (dx / widthPx) * s.spanDays;
  const n = raw < 0 ? -Math.round(-raw) : Math.round(raw);
  return n === 0 ? 0 : n; // -0 정규화 (Object.is(-0, 0) === false → toBe(0) 실패 방지)
}

/**
 * 스케일이 걸친 모든 달의 눈금. 좌단보다 앞선 1일은 좌단으로 클램프해 첫 눈금이 화면 밖으로 나가지 않게 한다.
 * isFuture = 눈금이 today 보다 뒤 (today 는 인자 — 시계를 읽지 않는다).
 */
export function monthTicks(s: Scale, todayIso: string): MonthTick[] {
  const todayDay = toDayIndex(todayIso);
  const first = new Date(s.originDay * DAY_MS);
  const last = new Date((s.originDay + s.spanDays - 1) * DAY_MS);
  const endY = last.getUTCFullYear();
  const endM = last.getUTCMonth();
  const out: MonthTick[] = [];
  let y = first.getUTCFullYear();
  let m = first.getUTCMonth();
  while (y < endY || (y === endY && m <= endM)) {
    const dayIndex = Math.max(Date.UTC(y, m, 1) / DAY_MS, s.originDay);
    out.push({
      dayIndex,
      label: `${String(y).slice(2)}.${String(m + 1).padStart(2, "0")}`,
      isFuture: dayIndex > todayDay,
    });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
}

/** "2026-07-16" → "26.07.16" (축 양단·툴팁용 짧은 표기). */
export function fmtShort(iso: string): string {
  const s = fromDayIndex(toDayIndex(iso)); // 검증 + 정규화
  return `${s.slice(2, 4)}.${s.slice(5, 7)}.${s.slice(8, 10)}`;
}
